import { toProgrammeShortId } from '@/lib/programmeId'
import { escapeFilterLiteral } from '@/lib/community/programmeQuery'
import { Author, ProgramStatus } from '@/domain'
import { authTokenLocalStorageKey } from '@/lib/api/lib/client'
import { AuthenticatedApiClient } from '@/lib/api/authenticatedClient'
import { FetchError } from '@/lib/fetchJson'

export interface ProgramPreview {
    id?: string
    origId: string
    language?: string
    data?: string
    status: ProgramStatus
    title: string
    description: string
    numberOfWorkouts: number
    meanExerciseDuration: number
    meanWorkoutDuration: number
    programmeDuration: number
    sexAtBirth: string
    level: string
    age: string
    goals: string
    price: number
    tags: string[]
    exercises: string[]
    author?: string
    expand?: ProgramPreviewExpand
    created?: string
    updated?: string
}

export interface ProgramPreviewExpand {
    author?: Author
}

// Authors above the plain "user" level (coaches, pros, ...).
export function isPremiumAuthor(programme: ProgramPreview): boolean {
    const level = programme.expand?.author?.level
    return !!level && level !== 'user'
}

const AUTHOR_FIELDS =
    'expand.author.login,expand.author.level,expand.author.bio,expand.author.id,expand.author.photo'

export class ProgramPreviewClient extends AuthenticatedApiClient {
    async createOrUpdateProgramPreview(
        lesson: ProgramPreview
    ): Promise<ProgramPreview> {
        let existingLesson: ProgramPreview | null = null
        try {
            existingLesson = lesson.id
                ? await this.fetchProgramPreviewByUID(lesson.id)
                : null
        } catch (error) {
            // fetchWithAuth throws FetchError; only a 404 means "no existing
            // preview, create one". Anything else (5xx, auth throws) must
            // propagate instead of silently falling through to a blind POST.
            if (
                !(error instanceof FetchError) ||
                error.response.status !== 404
            ) {
                throw error
            }
        }

        if (existingLesson) {
            return this.fetchWithAuth<ProgramPreview>(
                `${this.baseUrl}/${existingLesson.id}`,
                { method: 'PATCH', body: JSON.stringify(lesson) }
            )
        }

        return this.fetchWithAuth<ProgramPreview>(this.baseUrl, {
            method: 'POST',
            body: JSON.stringify(lesson)
        })
    }

    /**
     * Unpublish path: a draft must never create a community record (that
     * would count against the listing limit), only downgrade an existing one.
     */
    async updateProgramPreviewIfExists(
        lesson: ProgramPreview
    ): Promise<ProgramPreview | null> {
        if (!lesson.id) return null
        let existingLesson: ProgramPreview | null = null
        try {
            existingLesson = await this.fetchProgramPreviewByUID(lesson.id)
        } catch (error) {
            if (
                !(error instanceof FetchError) ||
                error.response.status !== 404
            ) {
                throw error
            }
        }
        if (!existingLesson) return null
        return this.fetchWithAuth<ProgramPreview>(
            `${this.baseUrl}/${existingLesson.id}`,
            { method: 'PATCH', body: JSON.stringify(lesson) }
        )
    }

    /**
     * Deleting a programme must take its community listing with it; leaving
     * the record behind keeps a dead entry in the collection forever. A
     * missing record is success, so the caller can retry safely.
     */
    async deleteProgramPreviewIfExists(uid: string): Promise<boolean> {
        if (!uid) return false
        try {
            await this.fetchWithAuth<null>(`${this.baseUrl}/${uid}`, {
                method: 'DELETE'
            })
            return true
        } catch (error) {
            if (error instanceof FetchError && error.response.status === 404) {
                return false
            }
            throw error
        }
    }

    async fetchProgramPreviewByUID(
        uid: string
    ): Promise<ProgramPreview | null> {
        const url = `${this.baseUrl}/${uid}?skipTotal=true&fields=*,${AUTHOR_FIELDS}&expand=author`
        return this.fetchWithAuth<ProgramPreview | null>(url, { method: 'GET' })
    }

    /**
     * A programme's preview by its full programme id. Previews published by
     * the app use the id's short form as their record id; records written
     * otherwise (imports, seeded data) do not, so a miss falls back to a
     * lookup by origId. Null when there is none.
     */
    async fetchProgramPreviewByProgrammeId(
        programId: string
    ): Promise<ProgramPreview | null> {
        try {
            return await this.fetchProgramPreviewByUID(
                toProgrammeShortId(programId)
            )
        } catch (error) {
            if (
                !(error instanceof FetchError) ||
                error.response.status !== 404
            ) {
                throw error
            }
        }
        const params = new URLSearchParams({
            filter: `origId='${escapeFilterLiteral(programId)}'`,
            perPage: '1',
            skipTotal: 'true',
            fields: `*,${AUTHOR_FIELDS}`,
            expand: 'author'
        })
        const res = await this.fetchWithAuth<{ items?: ProgramPreview[] }>(
            `${this.baseUrl}?${params.toString()}`,
            { method: 'GET' }
        )
        return res.items?.[0] ?? null
    }

    async fetchProgramPreviews(
        page?: number,
        perPage?: number,
        authorId?: string,
        filters?: string
    ): Promise<{ items: ProgramPreview[]; totalPages: number }> {
        const params = new URLSearchParams({
            sort: '-created',
            skipTotal: 'false',
            fields: `*,${AUTHOR_FIELDS}`,
            expand: 'author,exercises',
            perPage: String(perPage ?? 10),
            page: String(page ?? 1)
        })
        if (authorId) params.set('author', authorId)
        if (filters) params.set('filter', filters)

        const url = `${this.baseUrl}?${params.toString()}`
        const response = await this.fetchWithAuth<{
            items?: ProgramPreview[]
            totalPages: number
        }>(url, { method: 'GET' })

        return {
            items: response.items || [],
            totalPages: response.totalPages
        }
    }
}

export const lessonPreviewClient = new ProgramPreviewClient(
    `${import.meta.env.VITE_COLLECTIONS_HOST}/api/v1/collections/v1CommunityLessonsPreview`,
    () => localStorage.getItem(authTokenLocalStorageKey) || ''
)

/** queryCache key of one programme preview (by full or short programme id). */
export function communityPreviewKey(programId: string): string {
    return 'community:preview:' + toProgrammeShortId(programId)
}
